/**
 * Project heartbeat on Overview (WP-B / decision 23):
 * Active today (focuses + checkouts + last mention) + Mentioned in standup → transcript modal.
 */
import { useEffect, useRef, useState } from "react";
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from "@mui/material";
import { brainTokens } from "../../design/brainTheme";
import { AgentPanel, Icon, PersonAvatar } from "../../mui";
import {
  getStandupTranscript,
  type ProjectHeartbeatResponse,
  type StandupMentionRef,
  type TranscriptSection,
} from "../../lib/brain-api";

const c = brainTokens.color;

export function ProjectHeartbeatPanel({
  heartbeat,
}: {
  heartbeat: ProjectHeartbeatResponse | null;
}) {
  const [modal, setModal] = useState<StandupMentionRef | null>(null);

  if (!heartbeat) return null;
  const { activeToday, mentionedInStandup } = heartbeat;
  const hasFocuses = activeToday.focuses.length > 0;
  const hasCheckouts = activeToday.checkouts.length > 0;
  const mention = mentionedInStandup ?? activeToday.lastStandupMention;

  return (
    <>
      <AgentPanel context="Heartbeat">
        {!hasFocuses && !hasCheckouts && !mention ? (
          <Box sx={{ fontSize: 13.5, color: c.tealBodyInk }}>
            No linked standup focuses or checkouts yet. Focuses appear here once linked to this project.
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {hasFocuses && (
              <Box>
                <Box
                  sx={{
                    fontFamily: brainTokens.font.mono,
                    fontSize: 10.5,
                    fontWeight: 600,
                    letterSpacing: ".08em",
                    textTransform: "uppercase",
                    color: c.muted2,
                    mb: 0.75,
                  }}
                >
                  Standup focuses
                </Box>
                {activeToday.focuses.map((f) => (
                  <Box
                    key={f.id}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      py: 0.6,
                      borderBottom: `1px solid ${c.borderLight}`,
                      "&:last-of-type": { borderBottom: "none" },
                    }}
                  >
                    <PersonAvatar name={f.displayName} size={24} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box
                        sx={{
                          fontSize: 13.5,
                          color: f.done ? c.muted2 : c.ink2,
                          textDecoration: f.done ? "line-through" : "none",
                          lineHeight: 1.4,
                        }}
                      >
                        {f.text}
                      </Box>
                      {f.issueKey && (
                        <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 11, color: c.tealInk, mt: 0.2 }}>
                          {f.issueKey}
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {hasCheckouts && (
              <Box>
                <Box
                  sx={{
                    fontFamily: brainTokens.font.mono,
                    fontSize: 10.5,
                    fontWeight: 600,
                    letterSpacing: ".08em",
                    textTransform: "uppercase",
                    color: c.muted2,
                    mb: 0.75,
                  }}
                >
                  Checked out locally
                </Box>
                {activeToday.checkouts.map((ch) => (
                  <Box
                    key={`${ch.holder}-${ch.startedAt}`}
                    sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}
                  >
                    <PersonAvatar name={ch.displayName} size={24} presence="active" />
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ fontSize: 13.5, fontWeight: 600, color: c.ink2 }}>{ch.displayName}</Box>
                      <Box sx={{ fontSize: 12, color: c.muted2 }}>
                        since{" "}
                        {new Date(ch.startedAt).toLocaleString("en-GB", {
                          day: "numeric",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                        {ch.note ? ` · ${ch.note}` : ""}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {mention && (
              <Box sx={{ background: c.white, borderRadius: "8px", p: "10px 12px" }}>
                <Box
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: c.muted2,
                    mb: "4px",
                  }}
                >
                  Last standup mention · {mention.date}
                </Box>
                <Box sx={{ fontSize: 13, lineHeight: 1.55, color: c.ink3, mb: "8px" }}>{mention.summary}</Box>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Icon name="users" size={14} />}
                  onClick={() => setModal(mention)}
                  sx={{ textTransform: "none" }}
                >
                  Open transcript{mention.sectionId ? " at section" : ""}
                </Button>
              </Box>
            )}
          </Box>
        )}
      </AgentPanel>

      <StandupTranscriptModal
        open={!!modal}
        mention={modal}
        onClose={() => setModal(null)}
      />
    </>
  );
}

function StandupTranscriptModal({
  open,
  mention,
  onClose,
}: {
  open: boolean;
  mention: StandupMentionRef | null;
  onClose: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [transcript, setTranscript] = useState<string | null>(null);
  const [sections, setSections] = useState<TranscriptSection[]>([]);
  const [error, setError] = useState<string | null>(null);
  const sectionRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open || !mention) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setTranscript(null);
    getStandupTranscript(mention.date)
      .then((r) => {
        if (cancelled) return;
        if (!r) {
          setError("No transcript ingested for this day.");
          return;
        }
        setTranscript(r.transcript);
        setSections(r.sections);
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load transcript.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, mention]);

  useEffect(() => {
    if (!transcript || !mention?.sectionId) return;
    const t = window.setTimeout(() => {
      sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => window.clearTimeout(t);
  }, [transcript, mention?.sectionId]);

  const targetId = mention?.sectionId ?? null;
  const sectionMissing =
    !!targetId && sections.length > 0 && !sections.some((s) => s.id === targetId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: 18 }}>
        Standup transcript{mention ? ` · ${mention.date}` : ""}
      </DialogTitle>
      <DialogContent dividers sx={{ maxHeight: "70vh" }}>
        {mention?.summary && (
          <Box
            sx={{
              background: c.tealTint,
              borderRadius: "10px",
              p: "12px 14px",
              mb: 2,
              border: `1px solid ${c.tealTintBorder}`,
            }}
          >
            <Box
              sx={{
                fontFamily: brainTokens.font.mono,
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: ".08em",
                textTransform: "uppercase",
                color: c.tealInk,
                mb: 0.5,
              }}
            >
              AI summary
            </Box>
            <Box sx={{ fontSize: 14, lineHeight: 1.55, color: c.ink2 }}>{mention.summary}</Box>
          </Box>
        )}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={22} sx={{ color: c.teal }} />
          </Box>
        )}
        {error && <Box sx={{ fontSize: 13.5, color: c.dangerText }}>{error}</Box>}
        {sectionMissing && (
          <Box sx={{ fontSize: 13, color: c.muted2, mb: 1.5 }}>Section not found — showing full transcript.</Box>
        )}
        {transcript && (
          <TranscriptBody
            transcript={transcript}
            sections={sections}
            highlightId={sectionMissing ? null : targetId}
            sectionRef={sectionRef}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function TranscriptBody({
  transcript,
  sections,
  highlightId,
  sectionRef,
}: {
  transcript: string;
  sections: TranscriptSection[];
  highlightId: string | null;
  sectionRef: React.RefObject<HTMLDivElement | null>;
}) {
  if (sections.length === 0) {
    return (
      <Box
        sx={{
          fontFamily: brainTokens.font.mono,
          fontSize: 12.5,
          whiteSpace: "pre-wrap",
          color: c.ink3,
          lineHeight: 1.55,
        }}
      >
        {transcript}
      </Box>
    );
  }

  const blocks: { section: TranscriptSection | null; text: string }[] = [];
  for (let i = 0; i < sections.length; i++) {
    const start = sections[i].startOffset;
    const end = i + 1 < sections.length ? sections[i + 1].startOffset : transcript.length;
    blocks.push({ section: sections[i], text: transcript.slice(start, end) });
  }
  if (sections[0].startOffset > 0) {
    blocks.unshift({ section: null, text: transcript.slice(0, sections[0].startOffset) });
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {blocks.map((b, idx) => {
        const active = b.section && highlightId && b.section.id === highlightId;
        return (
          <Box
            key={b.section?.id ?? `pre-${idx}`}
            ref={active ? sectionRef : undefined}
            id={b.section ? `standup-section-${b.section.id}` : undefined}
            sx={{
              borderRadius: "8px",
              p: active ? "10px 12px" : 0,
              background: active ? c.tealTint : "transparent",
              boxShadow: active ? `inset 0 0 0 1px ${c.teal}` : "none",
            }}
          >
            {b.section && (
              <Box
                sx={{
                  fontFamily: brainTokens.font.sans,
                  fontSize: 14,
                  fontWeight: 600,
                  color: c.ink,
                  mb: 0.5,
                }}
              >
                {b.section.label}
              </Box>
            )}
            <Box
              sx={{
                fontFamily: brainTokens.font.mono,
                fontSize: 12.5,
                whiteSpace: "pre-wrap",
                color: c.ink3,
                lineHeight: 1.55,
              }}
            >
              {b.text.replace(/^(#{1,3}\s+.+)\n?/, "")}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
